import { ChangeEvent, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Typography, Box } from '@mui/material';
import { Alert, Button, Input, StrongModalText } from '@atoms';
import { PERMISSIONS } from '@globalConstants';
import { usePermissions, useURLparams } from '@hooks';
import { BoxContainer } from '@layouts';
import styles from './styles';
import useSegmentCreation from './useSegmentCreations';

/** Use form value state */
const useTextState = () => {
  const [value, setValue] = useState('');
  /**
   * Updates the value of the text input.
   */
  const onChange = (newValue: string) => {
    setValue(newValue);
  };
  /**
   * Resets the value of the text input to an empty string.
   */
  const resetInput = () => {
    setValue('');
  };

  return { value, onChange, resetInput };
};

/** Render add segment component */
const AddSegment = () => {
  const { t } = useTranslation();
  const isDemo = usePermissions([PERMISSIONS.demoCreateClient]);
  const { value, onChange, resetInput } = useTextState();
  const { params } = useURLparams<{ customerId: number }>();
  const selectedCompany = params?.customerId;
  const {
    createSegment,
    isLoading,
    alertMessage,
    isError,
    isSuccess,
    setIsError,
    setIsSuccess,
    showModalConfirm,
    setShowModalConfirm,
  } = useSegmentCreation();

  /** Handle submit */
  const handleSubmit = () => {
    setShowModalConfirm(true);
  };
  /** Handle submit */
  const handleModalConfirm = () => {
    createSegment(selectedCompany, value.trim()).then(() => {
      setShowModalConfirm(false);
      resetInput();
    });
  };
  return (
    <BoxContainer>
      <Typography variant='h4'>{t('COMMON:ADD_SEGMENT')}</Typography>
      <Box sx={styles.mainBox}>
        <Input
          placeholder={t('COMMON:SEGMENT')}
          value={value}
          onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
        />
        <Button
          customStyle={{ box: styles.buttonsBox }}
          text={'COMMON:CONFIRM'}
          variant='contained'
          onClick={handleSubmit}
          disabled={!(selectedCompany && value.trim())}
        />
      </Box>
      <Alert
        variant={'warning'}
        modalVisible={showModalConfirm}
        handleClose={() => setShowModalConfirm(false)}
        handleSubmit={handleModalConfirm}
        disabled={isDemo}
        closeButtonText={t('COMMON:CANCEL')}
        title={t('CLIENTS:CREATE_CLIENT:ADD_SEGMENT_MODAL:CONFIRMATION_MODAL_TITLE')}
        submitLoading={isLoading}
        content={
          <Trans
            i18nKey={'CLIENTS:CREATE_CLIENT:ADD_SEGMENT_MODAL:CONFIRMATION_MODAL_DESCRIPTION'}
            components={{ s: <StrongModalText /> }}
            values={{ segment: value?.trim() }}
          />
        }
      />
      <Alert
        modalVisible={isError || isSuccess}
        handleClose={() => {
          setIsError(false);
          setIsSuccess(false);
        }}
        {...alertMessage}
      />
    </BoxContainer>
  );
};

export default AddSegment;
